/**
 * Responsibility: decide where a row of wind turbines stands, and where each turbine in it goes.
 *
 * ## An installation, like the telescope arrays, with one difference that matters
 *
 * The site rule is the established one: a slot lattice, one authoritative `siteAt` consulted by both the
 * placement and the reservation, and ownership of each turbine settled by which tile it stands in — because a
 * row is far longer than the gap between tiles, so a site centred outside a tile still puts turbines inside it.
 *
 * The difference is the aim. **A telescope array reserves a circle over its whole site because it cannot know
 * its own rotation from a context with no terrain sampler** — its dishes are laid out along the contour, and the
 * reservation has no way to work out which way that runs. A row of turbines nearly 3,500 units long cannot
 * afford that: the circle would deny a vast area of farmland and leave a round clearing around every farm.
 *
 * So the aim comes from a roll. Nothing is lost — turbines face the prevailing wind, not the hill — and the
 * reservation becomes a capsule along the row it actually occupies, which is both the honest shape and a small
 * fraction of the ground.
 *
 * ## Three or none
 *
 * One turbine is a curiosity and two is a coincidence. Three in a line is unmistakably a wind farm, so a site
 * that cannot find three usable positions in a row builds nothing at all. And the run has to be *contiguous*:
 * dropping a turbine out of the middle of a row leaves a missing tooth, which reads worse than a shorter row.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { createLocalRng, deriveSeed } from './rng.js';
import { WIND_FARM } from '../config.js';

/** Linear interpolation, for turning a 0..1 roll into a range. */
function lerp(from, to, t) {
  return from + (to - from) * t;
}

/** Slot index a coordinate falls in. */
function slotAt(coordinate, spacing) {
  return Math.round(coordinate / spacing - 0.5);
}

/** Half the length of the longest row a site could lay, in world units. */
const MAX_HALF_LENGTH = ((WIND_FARM.MAX_TURBINES - 1) * WIND_FARM.TURBINE_SPACING) / 2;

/** How far anything belonging to a site can be from its centre. */
const SITE_REACH = MAX_HALF_LENGTH + WIND_FARM.BLADE_LENGTH;

/**
 * How far a site may be jittered from its slot centre.
 *
 * Clamped against the grid whatever the configured value, so raising `SITE_JITTER` past what the spacing allows
 * cannot let two farms overlap. Arithmetic rather than a collision check, the same guarantee the telescope
 * arrays and the city's streets rest on.
 */
const SITE_JITTER = Math.max(
  0,
  Math.min(WIND_FARM.SITE_JITTER, (WIND_FARM.SITE_SPACING - 2 * (SITE_REACH + WIND_FARM.CLEARANCE)) / 2),
);

/** Furthest any part of a site can be from its slot centre. Sets how wide the reservation has to look. */
const SLOT_INFLUENCE = SITE_JITTER + SITE_REACH + WIND_FARM.CLEARANCE;

/**
 * Build the shared site rule.
 *
 * @param {number} seed - Base world seed.
 * @param {string} biomeId - Derives an independent stream from everything else in the world.
 * @returns {(slotX: number, slotZ: number, sampleWeight: Function) => object|null}
 */
function createSites(seed, biomeId) {
  const siteSeed = deriveSeed(seed, `windFarm:${biomeId}`);

  return function siteAt(slotX, slotZ, sampleWeight) {
    const centerX = (slotX + 0.5) * WIND_FARM.SITE_SPACING;
    const centerZ = (slotZ + 0.5) * WIND_FARM.SITE_SPACING;

    const weight = sampleWeight(centerX, centerZ);
    if (weight <= 0) return null;

    /*
      One stream per slot, keyed by slot index rather than by tile, so a farm does not depend on which tile is
      being built or the order tiles are visited in.

      Roll order is part of the world's identity. Reordering these, or inserting one before an existing one,
      moves and re-aims every wind farm at every seed. Append only.
    */
    const random = createLocalRng(siteSeed, slotX, slotZ);

    const presenceRoll = random();
    const countRoll = random();
    const facingRoll = random();
    const heightRoll = random();
    const jitterRollX = random();
    const jitterRollZ = random();
    const phaseRoll = random();

    /*
      Density scales with biome weight, so a farm is less likely near a region's edge than in the middle of it.
      Unlike a scatter this does not thin a row out — a site either happens or it does not.
    */
    if (presenceRoll >= WIND_FARM.SITE_DENSITY * weight) return null;

    const count =
      WIND_FARM.MIN_TURBINES +
      Math.min(
        WIND_FARM.MAX_TURBINES - WIND_FARM.MIN_TURBINES,
        Math.floor(countRoll * (WIND_FARM.MAX_TURBINES - WIND_FARM.MIN_TURBINES + 1)),
      );

    const facing = facingRoll * Math.PI * 2;

    /*
      One height for the whole farm. A real wind farm is a batch of identical machines, so varying them inside a
      row would read as a mistake — varying between rows reads as different vintages, which is what it is.
    */
    const hubHeight =
      WIND_FARM.HUB_HEIGHT * lerp(1 - WIND_FARM.HEIGHT_VARIATION, 1 + WIND_FARM.HEIGHT_VARIATION, heightRoll);

    return {
      x: centerX + (jitterRollX * 2 - 1) * SITE_JITTER,
      z: centerZ + (jitterRollZ * 2 - 1) * SITE_JITTER,

      count,

      /*
        The row runs perpendicular to the way the turbines face, which is what a wind farm is: a line of
        machines across the wind, not along it. Derived rather than rolled separately so the two can never
        disagree.
      */
      facing,
      alongX: -Math.sin(facing),
      alongZ: Math.cos(facing),

      hubHeight,
      phase: phaseRoll * Math.PI * 2,
      reach: SITE_REACH,
      weight,
    };
  };
}

/** Where each turbine in a row would stand, before the ground has a say. */
function rowPositions(site) {
  const half = ((site.count - 1) * WIND_FARM.TURBINE_SPACING) / 2;
  const positions = [];

  for (let i = 0; i < site.count; i += 1) {
    const along = -half + i * WIND_FARM.TURBINE_SPACING;
    positions.push({
      x: site.x + site.alongX * along,
      z: site.z + site.alongZ * along,
      index: i,
    });
  }

  return positions;
}

/**
 * The longest unbroken run of positions the ground will accept.
 *
 * **Contiguous, not merely "the ones that fit".** A turbine missing from the middle of a row reads as a fault;
 * a shorter row reads as a smaller farm. So the run is taken whole and a site offering fewer than
 * `MIN_TURBINES` in a row builds nothing.
 *
 * @param {Array<object>} positions
 * @param {(x: number, z: number) => {elevation: number, slope: number}} sampleGroundDetail
 * @param {(x: number, z: number, radius: number) => boolean} [isReserved]
 * @returns {Array<object>|null}
 */
function longestUsableRun(positions, sampleGroundDetail, isReserved) {
  const reach = WIND_FARM.BLADE_LENGTH + WIND_FARM.CLEARANCE;

  let best = null;
  let current = [];

  for (const position of positions) {
    const ground = sampleGroundDetail(position.x, position.z);

    const usable =
      ground.slope <= WIND_FARM.MAX_GROUND_SLOPE && !isReserved?.(position.x, position.z, reach);

    if (usable) {
      current.push({ ...position, groundY: ground.elevation });
      if (best === null || current.length > best.length) best = current;
    } else if (current.length > 0) {
      /*
        A fresh array rather than emptying this one, because `best` may still be pointing at it — and it is the
        run *before* the gap that has to survive.
      */
      current = [];
    }
  }

  return best !== null && best.length >= WIND_FARM.MIN_TURBINES ? best : null;
}

/**
 * Range of slots whose sites could reach into a region.
 *
 * Not the "slots whose centre falls inside" rule the scatters use: a row is thousands of units long, so a site
 * centred well outside a tile still puts turbines inside it. Ownership is settled per turbine instead.
 */
function slotsNear(region) {
  const spacing = WIND_FARM.SITE_SPACING;

  return {
    firstX: slotAt(region.originX - SLOT_INFLUENCE, spacing),
    lastX: slotAt(region.originX + region.size + SLOT_INFLUENCE, spacing),
    firstZ: slotAt(region.originZ - SLOT_INFLUENCE, spacing),
    lastZ: slotAt(region.originZ + region.size + SLOT_INFLUENCE, spacing),
  };
}

/**
 * Build the wind farm placement function for a biome.
 *
 * @param {number} seed - Base world seed.
 * @param {string} biomeId
 * @returns {(region: object, context: object) => Array<object>}
 */
export function createWindFarm(seed, biomeId) {
  const siteAt = createSites(seed, biomeId);

  /**
   * @param {{originX: number, originZ: number, size: number}} region
   * @param {object} context
   * @returns {Array<object>} Turbine descriptions, only those standing inside the region.
   */
  return function collectStructures(region, { sampleWeight, sampleGroundDetail, isReserved }) {
    const slots = slotsNear(region);
    const turbines = [];

    for (let slotX = slots.firstX; slotX <= slots.lastX; slotX += 1) {
      for (let slotZ = slots.firstZ; slotZ <= slots.lastZ; slotZ += 1) {
        const site = siteAt(slotX, slotZ, sampleWeight);
        if (!site) continue;

        const run = longestUsableRun(rowPositions(site), sampleGroundDetail, isReserved);
        if (run === null) continue;

        for (const position of run) {
          /*
            One tile owns each turbine, decided by which tile it stands in. The row is shared between tiles, so
            this is the only place ownership can be settled — getting it wrong emits a turbine twice, at double
            brightness, which is the mistake farmland's own fields once made.
          */
          if (position.x < region.originX || position.x >= region.originX + region.size) continue;
          if (position.z < region.originZ || position.z >= region.originZ + region.size) continue;

          turbines.push({
            kind: 'windTurbine',

            x: position.x,
            z: position.z,
            groundY: position.groundY,

            hubHeight: site.hubHeight,

            /*
              Shared across the row, which is what makes it read as one installation rather than a line of
              unrelated machines. The same call the telescope array makes about aiming its dishes together.
            */
            facing: site.facing,

            /*
              Rotor angle, and the one thing that differs from turbine to turbine.
              **This is what "several models with the blades in different positions" actually wants.** A handful
              of fixed poses would repeat visibly down a row of nine; one angle per turbine, offset from the
              farm's own, gives every one of them a different attitude for no extra geometry at all.
            */
            phase: site.phase + (position.index * Math.PI * 2) / WIND_FARM.BLADES + position.index * 0.37,

            weight: site.weight,

            /*
              The turbine's own dimensions, carried on the structure rather than read from config by the emitter.
              `structureGeometry.js` has no imports at all, so it can be reasoned about and tested without the
              world's tuning, and every other structure there works the same way — a windpump carries its wheel
              radius, a silo its height.
            */
            towerBaseRadius: WIND_FARM.TOWER_BASE_RADIUS,
            towerTopRatio: WIND_FARM.TOWER_TOP_RATIO,
            towerSides: WIND_FARM.TOWER_SIDES,
            towerRings: WIND_FARM.TOWER_RINGS,
            nacelleLength: WIND_FARM.NACELLE_LENGTH,
            nacelleWidth: WIND_FARM.NACELLE_WIDTH,
            nacelleHeight: WIND_FARM.NACELLE_HEIGHT,
            blades: WIND_FARM.BLADES,
            bladeLength: WIND_FARM.BLADE_LENGTH,
            bladeRootWidth: WIND_FARM.BLADE_ROOT_WIDTH,
            bladeChords: WIND_FARM.BLADE_CHORDS,
            hubRadius: WIND_FARM.HUB_RADIUS,
          });
        }
      }
    }

    return turbines;
  };
}

/**
 * Build the test that says whether a point is inside a wind farm.
 *
 * Published as part of the host biome's reservation so trees, buildings and the railway keep clear.
 *
 * **A capsule along the row rather than a circle over the site**, which the terrain-free aim is what buys: the
 * telescope array cannot know its own rotation here and has to reserve a disc, and a disc around a row this long
 * would deny an absurd amount of farmland. Like every other installation clearance it skips the ground checks,
 * so it reserves space around a handful of rows that were never built — that costs a tree here and there and
 * buys a test that needs no terrain sampling and answers at any coordinate.
 *
 * @param {number} seed - Base world seed.
 * @param {string} biomeId
 * @returns {(x: number, z: number, radius: number, context: object) => boolean}
 */
export function createWindFarmClearance(seed, biomeId) {
  const siteAt = createSites(seed, biomeId);

  return function isInsideAWindFarm(x, z, radius, { sampleWeight, cache }) {
    const spacing = WIND_FARM.SITE_SPACING;

    /*
      Only the slots whose site could actually reach this point. Every tree and farm building in the world asks
      this question once, so the bound matters — the telescope array measured a forest tile going from 7.9 ms to
      31.7 ms without it.
    */
    const margin = SLOT_INFLUENCE + radius;
    const firstX = slotAt(x - margin, spacing);
    const lastX = slotAt(x + margin, spacing);
    const firstZ = slotAt(z - margin, spacing);
    const lastZ = slotAt(z + margin, spacing);

    for (let slotX = firstX; slotX <= lastX; slotX += 1) {
      for (let slotZ = firstZ; slotZ <= lastZ; slotZ += 1) {
        const key = `windFarm:${slotX},${slotZ}`;

        // Memoised per tile, and not optional, for the reason above.
        let site = cache?.get(key);

        if (site === undefined) {
          site = siteAt(slotX, slotZ, sampleWeight);
          cache?.set(key, site);
        }

        if (!site) continue;

        /*
          Distance to the row itself: projected onto the line of turbines and clamped to its ends, which is a
          capsule. One dot product and a clamp, against nine distance tests for a per-turbine check.
        */
        const half = ((site.count - 1) * WIND_FARM.TURBINE_SPACING) / 2;
        const along = (x - site.x) * site.alongX + (z - site.z) * site.alongZ;
        const clamped = along < -half ? -half : along > half ? half : along;

        const nearestX = site.x + site.alongX * clamped;
        const nearestZ = site.z + site.alongZ * clamped;

        if (
          Math.hypot(x - nearestX, z - nearestZ) <
          WIND_FARM.BLADE_LENGTH + WIND_FARM.CLEARANCE + radius
        ) {
          return true;
        }
      }
    }

    return false;
  };
}

/** Exported so tests and budgets can reason about a farm's extent. */
export { SITE_REACH as WIND_FARM_REACH, MAX_HALF_LENGTH as WIND_FARM_MAX_HALF_LENGTH };
